import React from 'react'

const FALLBACK_SRC = '/images/empty.jpeg'
// Listing-card photos render between ~220px and ~45vw wide. A single 640px
// optimized source covers DPR2/3 across those slots; we deliberately use ONE
// width (no srcset) so there is exactly one load-triggering attribute (`src`),
// keeping JSX prop order deterministic across SSR/CSR.
const CARD_IMG_WIDTH = 640

export interface CardImageProps {
  src: string
  alt: string
  /** Maps to next/image `priority`: eager load + high fetchpriority. */
  eager?: boolean
}

/**
 * Hydration-safe listing-card image. Plain <img> (NOT <ImageWithFallback fill>)
 * pointed at the Next image optimizer for AVIF/WebP + on-the-fly resize.
 *
 * Why not next/image: its `fill` mode injects a 7-property inline style block
 * (position:absolute;…;color:transparent). On mobile, next/image's load handler
 * clears that live `style` attribute during hydration, so the SSR HTML carries
 * the style and the client's first paint doesn't → React 19 #418 on every
 * search / city / hood card-grid pageview (confirmed at-error on
 * /pension-estacionamiento/ciudad-de-mexico, Chrome Mobile, Telcel, 2026-06).
 * `fill` is replaced by `absolute inset-0 size-full object-cover`, so every
 * consumer MUST wrap this in a `relative` box that has a height (an aspect-ratio
 * box or h-full). `src` is LAST in JSX because React 19 sets `src` last on the
 * client (it defers the load); SSR must serialize it last too or the attribute
 * order drifts. See CLAUDE.md "Trigger J" / "Trigger D Phase 4" + Images.tsx.
 */
const CardImage = ({ src, alt, eager }: CardImageProps) => {
  const optimized =
    src && src.startsWith('http')
      ? `/_next/image?url=${encodeURIComponent(src)}&w=${CARD_IMG_WIDTH}&q=75`
      : src || FALLBACK_SRC
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt={alt}
      className="absolute inset-0 size-full object-cover"
      loading={eager ? undefined : 'lazy'}
      fetchPriority={eager ? 'high' : undefined}
      onError={(e) => {
        ;(e.currentTarget as HTMLImageElement).src = FALLBACK_SRC
      }}
      src={optimized}
    />
  )
}

export default React.memo(CardImage)
